import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { CesCheckableMenuComponent } from '../../ui/ces-checkable-menu/ces-checkable-menu.component';
import { CesCheckableMenuItem } from '../../ui/ces-checkable-menu/ces-checkable-menu.directive';
import { CesButtonSecondaryComponent } from '../../ui/ces-buttons/ces-button-secondary.component';
import { ButtonSize } from '../../ui/ces-buttons/interfaces';

export interface MeasureColumnVisibility {
  status: boolean;
  calculated?: boolean;
  verification: boolean;
  parameters?: boolean;
}

export type MeasureColumnToggleMode = 'automatic' | 'analysisPost';

const ALL_HIDDEN_AUTOMATIC: MeasureColumnVisibility = {
  status: false,
  calculated: false,
  verification: false,
};

const ALL_VISIBLE_AUTOMATIC: MeasureColumnVisibility = {
  status: true,
  calculated: true,
  verification: true,
};

const ALL_HIDDEN_ANALYSIS: MeasureColumnVisibility = {
  status: false,
  verification: false,
  parameters: false,
};

const ALL_VISIBLE_ANALYSIS: MeasureColumnVisibility = {
  status: true,
  verification: true,
  parameters: true,
};

@Component({
  selector: 'app-measure-column-toggle',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CesButtonSecondaryComponent, CesCheckableMenuComponent, TranslocoModule],
  templateUrl: './measure-column-toggle.component.html',
  styleUrl: './measure-column-toggle.component.scss',
})
export class MeasureColumnToggleComponent {
  private readonly translate = inject(TranslocoService);
  private readonly translations = toSignal(this.translate.selectTranslation());

  readonly value = input<MeasureColumnVisibility>(ALL_HIDDEN_AUTOMATIC);
  readonly mode = input<MeasureColumnToggleMode>('automatic');
  readonly size = input<ButtonSize>(undefined);
  readonly visibilityChange = output<MeasureColumnVisibility>();

  readonly menuItems = computed<CesCheckableMenuItem[]>(() => {
    this.translations();
    const current = this.value();
    const items: CesCheckableMenuItem[] = [
      {
        label: this.translate.translate('common.labels.status'),
        checkable: true,
        checked: current.status,
        command: () => this.toggle('status'),
      },
    ];

    if (this.mode() === 'automatic') {
      items.push(
        {
          label: this.translate.translate('common.labels.calculatedValue'),
          checkable: true,
          checked: current.calculated ?? false,
          command: () => this.toggle('calculated'),
        },
        {
          label: this.translate.translate('common.labels.verification'),
          checkable: true,
          checked: current.verification,
          command: () => this.toggle('verification'),
        },
      );
    } else {
      items.push(
        {
          label: this.translate.translate('common.labels.verification'),
          checkable: true,
          checked: current.verification,
          command: () => this.toggle('verification'),
        },
        {
          label: this.translate.translate('common.labels.parameters'),
          checkable: true,
          checked: current.parameters ?? false,
          command: () => this.toggle('parameters'),
        },
      );
    }

    return items;
  });

  toggle(column: keyof MeasureColumnVisibility): void {
    const current = this.value();
    this.visibilityChange.emit({ ...current, [column]: !(current[column] ?? false) });
  }

  selectAll(): void {
    const all = this.mode() === 'analysisPost' ? ALL_VISIBLE_ANALYSIS : ALL_VISIBLE_AUTOMATIC;
    this.visibilityChange.emit({ ...all });
  }

  deselectAll(): void {
    const all = this.mode() === 'analysisPost' ? ALL_HIDDEN_ANALYSIS : ALL_HIDDEN_AUTOMATIC;
    this.visibilityChange.emit({ ...all });
  }
}
